/**
 * Centralized person-name validation for the monorepo.
 *
 * The same rule is enforced on the backend by `StringUtils.isValidName` /
 * `StringUtils.isValidThaiAndEnglishText` in set-api-monorepo — keep them in sync. The
 * frontend charset is deliberately the stricter of the two (it excludes Thai digits and
 * `฿`), so the backend never rejects a value this accepts.
 */

// Thai characters (ก-ฮ), English letters (a-z, A-Z), spaces, and:
// - Period (.) for abbreviations or initials (e.g., "Dr. John")
// - Apostrophe (') for names with contractions or possessives (e.g., "O'Connor")
// - Hyphen (-) for hyphenated names (e.g., "Anne-Marie")
export const NAME_ALLOWED_CHARACTERS = /^[a-zA-Z฀-ฺเ-๎\s.'-]+$/;

// At least one real letter: Latin, a Thai consonant (U+0E01-U+0E2E, ก-ฮ), or a base vowel
// (U+0E30 ะ, U+0E32 า, U+0E33 ำ, U+0E40-U+0E44 เ แ โ ใ ไ).
//
// Deliberately excluded, because they are marks rather than letters and must not by
// themselves satisfy "contains a letter":
//   - U+0E2F ฯ  abbreviation mark
//   - U+0E45 ๅ  vowel-length sign
//   - U+0E46 ๆ  repetition mark
//   - U+0E31, U+0E34-U+0E3A, U+0E47-U+0E4E   combining vowel/tone marks
//
// Every real Thai syllable needs a consonant in the range above, so no legitimate name is
// rejected by this. Kept in sync with NAME_LETTER in set-api-monorepo's StringUtils.
const NAME_LETTER = /[A-Za-zก-ฮะาำเ-ไ]/;

export const NAME_ALLOWED_CHARACTERS_ERROR_MESSAGE = 'อนุญาตเฉพาะ ก-ฮ, a-z, A-Z เท่านั้น';

export const NAME_MUST_CONTAIN_LETTER_ERROR_MESSAGE =
  'รูปแบบไม่ถูกต้อง ต้องมีตัวอักษรอย่างน้อย 1 ตัว ไม่สามารถกรอกเฉพาะเครื่องหมายพิเศษได้';

/**
 * The two halves of the name rule are exposed separately so a form can report *which* one
 * failed — the charset message and the must-contain-a-letter message are different copy.
 * Use `isValidName` when one combined message is enough.
 */
export const isValidNameCharset = (value: string) => NAME_ALLOWED_CHARACTERS.test(value);

/**
 * The charset allows `.`, `'` and `-`, so without this a bare `-` would pass. Upstream
 * systems (citizen-card OCR, imported examinee sheets) send `-` to mean "no name given",
 * and it must not be stored as a real one.
 *
 * ponytail: reject-only for now. If we later normalize `-` → null, do it at ingest and
 * keep this check as the last line of defense.
 */
export const hasNameLetter = (value: string) => NAME_LETTER.test(value);

/**
 * A name must be in the charset AND contain at least one real letter.
 *
 * Pass this to zod's `.refine()` *after* `.trim()`, so `'  -  '` is caught too.
 */
export const isValidName = (value: string) => isValidNameCharset(value) && hasNameLetter(value);

/** Same rule as `isValidName`, plus middle name being optional so `''` is valid. */
export const isValidMiddleName = (value: string) => value === '' || isValidName(value);
